import React, { useMemo } from "react";
import { Button, Modal, Row, Typography, Form, FormInstance, Col, Input, Select, Checkbox } from "antd";
import FormLabel from "src/components/AntdCustomPart/FormLabel/FormLabel";
import { generateErrorMessage } from "src/utils/error-message";
import { AreaProps } from "src/entity/area/area.entity";
import { EmissionObject, EmissionObjectEnum } from "src/entity/common/unit.constant";

const { Text } = Typography;

interface InformationModalProps {
  submitOthersInformation: () => void;
  loadingAll: {
    mainData: boolean;
    distinctColumn: boolean;
    distinctYear: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
  };
  visibleModal: boolean;
  closeModal: () => void;
  errorOthers: string | null;
  form: FormInstance;
  clearErrorOthers: () => void;
  dataAreaInCompany: AreaProps[];
}

export enum EmissionTypeEnum {
  FILL_EMISSION = "fillEmission",
  STACK_SAMPLING = "stackSampling",
}

const calculationMethods: { label: string; value: EmissionTypeEnum }[] = [
  { label: "Fill Emission", value: EmissionTypeEnum.FILL_EMISSION },
  { label: "Stack Sampling", value: EmissionTypeEnum.STACK_SAMPLING },
];

const InformationModal: React.FC<InformationModalProps> = (props) => {
  const { submitOthersInformation, loadingAll, closeModal, visibleModal, errorOthers, form, clearErrorOthers, dataAreaInCompany } = props;

  const optionsAreaInCompany = useMemo(() => {
    return dataAreaInCompany.map((item) => ({
      label: item.areaName,
      value: item._id,
    }));
  }, [dataAreaInCompany]);

  return (
    <Modal
      title="Information"
      open={visibleModal}
      width={1000}
      onCancel={closeModal}
      footer={
        <Row className="w-full" justify={"end"}>
          <Button key="submit" type="primary" loading={loadingAll.update || loadingAll.create} onClick={submitOthersInformation}>
            Save
          </Button>
        </Row>
      }
      maskClosable={false}
      centered
      destroyOnClose
    >
      {errorOthers && (
        <Row className="w-full" justify={"center"}>
          <Text type="danger">{errorOthers}</Text>
        </Row>
      )}
      <Form
        size="middle"
        name={"setup-others-information"}
        layout="vertical"
        autoComplete="off"
        form={form}
        requiredMark={false}
        onFieldsChange={(_) => {
          if (errorOthers) {
            clearErrorOthers();
            form.setFields([
              {
                name: "sourceName",
                errors: undefined,
              },
              {
                name: "area",
                errors: undefined,
              },
            ]);
          }
        }}
      >
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12} lg={12}>
            <Form.Item
              label={<FormLabel label={"Source Name"} required />}
              name="sourceName"
              rules={[
                {
                  required: true,
                  message: generateErrorMessage("Source Name"),
                },
              ]}
            >
              <Input maxLength={50} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={12}>
            <Form.Item
              label={<FormLabel label={"Area"} required />}
              name="area"
              rules={[
                {
                  required: true,
                  message: generateErrorMessage("Area"),
                },
              ]}
            >
              <Select options={optionsAreaInCompany} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} lg={12}>
            <Form.Item
              label={<FormLabel label={"Emission"} required />}
              name="emission"
              rules={[
                {
                  required: true,
                  message: generateErrorMessage("emission"),
                },
              ]}
            >
              <Checkbox.Group options={EmissionObject} defaultValue={[EmissionObjectEnum.TVOC]} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} lg={12}>
            <Form.Item
              label={<FormLabel label={"Calculation Method"} required />}
              name="calculationMethod"
              rules={[
                {
                  required: true,
                  message: generateErrorMessage("Calculation Method"),
                },
              ]}
            >
              <Select options={calculationMethods} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default InformationModal;
